import { useMemo } from 'react';

import type { InvoiceQuerySchema } from '@/_pages/invoices/shared/schemas';

import { formatAddressBlock, pickBillingAddresses } from './address-utils';
import type { InvoiceAgreementLink } from './types';

export type InvoiceDetailsDerived = {
  /** Unique agreements on the invoice (by numeric id) for profile links. */
  agreementLinks: InvoiceAgreementLink[];
  terms: string;
  purchaseOrders: string;
  billingAddressBlocks: string[];
};

export function useInvoiceDetailsDerived(selectedInvoice: InvoiceQuerySchema | null): InvoiceDetailsDerived {
  return useMemo(() => {
    const agreementLinksSource = selectedInvoice?.agreement_links ?? [];

    const agreementLinks: InvoiceAgreementLink[] = [];
    const seenAgreementIds = new Set<number>();
    for (const link of agreementLinksSource) {
      const agreement = link.agreement;
      if (!agreement) {
        continue;
      }
      if (seenAgreementIds.has(agreement.id)) {
        continue;
      }
      seenAgreementIds.add(agreement.id);
      agreementLinks.push({
        id: agreement.id,
        agreementId: agreement.agreement_id?.trim() ? agreement.agreement_id : String(agreement.id),
      });
    }

    const terms = Array.from(
      new Set(
        agreementLinksSource
          .map((link) => link.agreement?.billing_term?.name ?? null)
          .filter((value): value is string => Boolean(value))
      )
    ).join(', ');

    const purchaseOrders = Array.from(
      new Set(
        agreementLinksSource
          .map((link) => link.agreement?.purchase_order ?? null)
          .filter((value): value is string => Boolean(value))
      )
    ).join(', ');

    const seenAddresses = new Set<string>();
    const billingAddressBlocks: string[] = [];
    for (const link of agreementLinksSource) {
      const agreement = link.agreement;
      if (!agreement) {
        continue;
      }
      for (const address of pickBillingAddresses(agreement.addresses)) {
        const formatted = formatAddressBlock(address);
        if (!formatted || seenAddresses.has(formatted)) {
          continue;
        }
        seenAddresses.add(formatted);
        billingAddressBlocks.push(formatted);
      }
    }

    return {
      agreementLinks,
      terms,
      purchaseOrders,
      billingAddressBlocks,
    };
  }, [selectedInvoice]);
}
